import { useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import DealsKanban from './DealsKanban';
import DealsTable from './DealsTable';
import DealModal from './DealModal';
import logger from '../../utils/logger';

function DealsBoard({
  pipelines,
  stages,
  deals,
  allDeals,
  companies,
  contacts,
  selectedPipelineId,
  viewMode,
  busy,
  onReload,
  onPipelineChange,
  onViewChange,
  onDealsChange,
  onCompaniesChange,
  onContactsChange,
  onCreateTaskForDeal,
  onOpenTasksForDeal
}) {
  const { request, showStatus } = useApi();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeDeal, setActiveDeal] = useState(null);
  const sourceDeals = allDeals || deals;

  const pipeline = useMemo(
    () => pipelines.find((item) => item.id === selectedPipelineId),
    [pipelines, selectedPipelineId]
  );
  const stageMap = useMemo(() => new Map(stages.map((stage) => [stage.id, stage])), [stages]);
  const columns = useMemo(
    () => stages.map((stage) => ({
      key: stage.id,
      title: stage.name,
      backgroundColor: stage.background_color,
      textColor: stage.text_color,
      tasks: deals.filter((deal) => deal.stage_id === stage.id)
    })),
    [stages, deals]
  );

  const handleOpenCreate = () => {
    setActiveDeal(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (deal) => {
    setActiveDeal(deal);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setActiveDeal(null);
  };

  const handleSaveDeal = async (payload) => {
    try {
      if (activeDeal) {
        const updated = await request(`/api/v1/crm/deals/${activeDeal.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
        onDealsChange(sourceDeals.map((deal) => (deal.id === updated.id ? updated : deal)));
        showStatus('success', 'Сделка обновлена.');
      } else {
        const created = await request('/api/v1/crm/deals', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        onDealsChange([created, ...sourceDeals]);
        showStatus('success', 'Сделка создана.');
      }
      handleCloseModal();
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось сохранить сделку.');
    }
  };

  const handleDeleteDeal = async (deal) => {
    if (!deal || !window.confirm(`Удалить сделку "${deal.title}"?`)) return;
    try {
      await request(`/api/v1/crm/deals/${deal.id}`, { method: 'DELETE' });
      onDealsChange(sourceDeals.filter((item) => item.id !== deal.id));
      setModalOpen(false);
      setActiveDeal(null);
      await onReload?.();
      showStatus('success', 'Сделка удалена.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось удалить сделку.');
    }
  };

  const handleCreateCompany = async (payload) => {
    try {
      const created = await request('/api/v1/crm/companies', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      onCompaniesChange([created, ...companies]);
      showStatus('success', 'Компания добавлена.');
      return created;
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось создать: ' + (error?.message || ''));
      return null;
    }
  };

  const handleCreateContact = async (payload) => {
    try {
      const created = await request('/api/v1/crm/contacts', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      onContactsChange([created, ...contacts]);
      showStatus('success', 'Контакт добавлен.');
      return created;
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось создать: ' + (error?.message || ''));
      return null;
    }
  };

  const handleDropStage = async (dealId, stageId) => {
    try {
      const updated = await request(`/api/v1/crm/deals/${dealId}`, {
        method: 'PATCH',
        body: JSON.stringify({ stage_id: stageId })
      });
      onDealsChange(sourceDeals.map((deal) => (deal.id === updated.id ? updated : deal)));
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось обновить стадию сделки.');
    }
  };

  const handleConfirmRetainer = async (deal) => {
    try {
      const updated = await request(`/api/v1/crm/deals/${deal.id}/confirm-retainer`, {
        method: 'POST'
      });
      onDealsChange(sourceDeals.map((item) => (item.id === updated.id ? updated : item)));
      await onReload?.();
      showStatus('success', 'Период подтвержден.');
    } catch (error) {
      logger.error(error);
      showStatus('error', 'Не удалось подтвердить период.');
    }
  };

  return (
    <div className="crm">
      <div className="crm__toolbar">
        <div className="crm__toolbar-left">
          <label>
            Воронка
            <select value={selectedPipelineId || ''} onChange={(event) => onPipelineChange(event.target.value)}>
              {pipelines.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </label>
          <div className="crm__view">
            <button type="button" className={viewMode === 'kanban' ? 'active' : ''} onClick={() => onViewChange('kanban')}>
              Канбан
            </button>
            <button type="button" className={viewMode === 'list' ? 'active' : ''} onClick={() => onViewChange('list')}>
              Список
            </button>
          </div>
        </div>
        <button type="button" onClick={handleOpenCreate} disabled={busy}>
          Новая сделка
        </button>
      </div>

      {viewMode === 'kanban' ? (
        <DealsKanban columns={columns} onOpenDeal={handleOpenEdit} onDropStage={handleDropStage} onConfirmRetainer={handleConfirmRetainer} />
      ) : (
        <DealsTable deals={deals} stages={stages} stageMap={stageMap} pipeline={pipeline} onOpenDeal={handleOpenEdit} />
      )}

      <DealModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveDeal}
        deal={activeDeal}
        pipeline={pipeline}
        stages={stages}
        companies={companies}
        contacts={contacts}
        onCreateCompany={handleCreateCompany}
        onCreateContact={handleCreateContact}
        onDelete={handleDeleteDeal}
        onCreateTask={onCreateTaskForDeal}
        onOpenTasks={onOpenTasksForDeal}
      />
    </div>
  );
}

export default DealsBoard;
